import {
    Directive,
    EmbeddedViewRef,
    Input,
    OnChanges,
    SimpleChange,
    SimpleChanges,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';

export type KalTemplateOutletCallback = (node: HTMLElement[], context?: Record<any, any>) => void;

/**
 * override du ngTemplateOutlet qui est trop succint
 * permet d'avoir une information comme quoi le template a été généré et de récupérer le contenu.
 * Pas possible de faire un simple "extends" car tous les champs sont en privés et non en protected ...
 */

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: '[kalTemplateOutlet]',
    standalone: false
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class KalTemplateOutlet implements OnChanges {
    /**
     * A context object to attach to the {@link EmbeddedViewRef}. This should be an
     * object, the object's keys will be available for binding by the local template `let`
     * declarations.
     * Using the key `$implicit` in the context object will set its value as default.
     */
    @Input() public kalTemplateOutletContext: Record<any, any> | null = null;
    /**
     * A string defining the template reference and optionally the context object for the template.
     */
    @Input() public kalTemplateOutlet: TemplateRef<any> | null = null;

    /**
     * function called when view has change
     */
    @Input() public kalTemplateOutletCallback: KalTemplateOutletCallback = null;

    private viewRef: EmbeddedViewRef<any> | null = null;

    constructor(private _viewContainerRef: ViewContainerRef) {
    }

    private static hasContextShapeChanged(ctxChange: SimpleChange): boolean {
        const prevCtxKeys = Object.keys(ctxChange.previousValue || {});
        const currCtxKeys = Object.keys(ctxChange.currentValue || {});

        if (prevCtxKeys.length === currCtxKeys.length) {
            for (const propName of currCtxKeys) {
                if (prevCtxKeys.indexOf(propName) === -1) {
                    return true;
                }
            }
            return false;
        }
        return true;
    }

    /**
     * We need to re-create existing embedded view if:
     * - templateRef has changed
     * - context has changes
     *
     * We mark context object as changed when the corresponding object
     * shape changes (new properties are added or existing properties are removed).
     * In other words we consider context with the same properties as "the same" even
     * if object reference changes (see https://github.com/angular/angular/issues/13407).
     */
    private shouldRecreateView(changes: SimpleChanges): boolean {
        const ctxChange = changes.kalTemplateOutletContext;
        return !!changes.kalTemplateOutlet || (ctxChange && KalTemplateOutlet.hasContextShapeChanged(ctxChange));
    }

    private updateExistingContext(ctx: Record<any, any>): void {
        for (const propName of Object.keys(ctx)) {
            (this.viewRef.context)[propName] = (this.kalTemplateOutletContext)[propName];
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        const recreateView = this.shouldRecreateView(changes);

        if (recreateView) {
            const viewContainerRef = this._viewContainerRef;

            if (this.viewRef) {
                viewContainerRef.remove(viewContainerRef.indexOf(this.viewRef));
            }

            this.viewRef = this.kalTemplateOutlet ?
                viewContainerRef.createEmbeddedView(this.kalTemplateOutlet, this.kalTemplateOutletContext) :
                null;

            if (this.viewRef && this.kalTemplateOutletCallback) {
                this.kalTemplateOutletCallback(this.viewRef.rootNodes, this.kalTemplateOutletContext);
            }
        } else if (this.viewRef && this.kalTemplateOutletContext) {
            this.updateExistingContext(this.kalTemplateOutletContext);
        }
    }
}
